import type * as Api from '@ViewModels';
import * as React from 'react';
import {
	getAiSuggestionsArchivePreviewCount,
	invalidateAllContentCalendarSuggestions,
	useAiSuggestionsArchivePreviewMutation,
	useArchiveAiSuggestionsInRangeMutation,
} from '../../../queries';
import type { IConfirmationDialogOption } from '../../components/ConfirmationDialog';
import { type IAiCalendarClearRange, getAiCalendarClearRange } from './getAiCalendarClearRange';

const ClearOptions: IConfirmationDialogOption<boolean>[] = [
	{ isDestructive: true, representedObject: true, title: 'Clear suggestions' },
	{ isCancel: true, representedObject: false, title: 'Cancel' },
];

const NothingToClearOptions: IConfirmationDialogOption<boolean>[] = [
	{ isCancel: true, representedObject: false, title: 'Close' },
];

export const useClearAiSuggestions = ({
	impersonationContext,
	onCleared,
	onError,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onCleared?: () => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
}) => {
	const [previewCount, setPreviewCount] = React.useState<number | null>(null);
	const [previewedRange, setPreviewedRange] = React.useState<IAiCalendarClearRange | null>(null);

	const { mutate: previewClear, isPending: isPreviewing } = useAiSuggestionsArchivePreviewMutation({
		onSuccess: preview => setPreviewCount(getAiSuggestionsArchivePreviewCount(preview)),
		onError,
	});

	const { mutate: archiveInRange, isPending: isArchiving } = useArchiveAiSuggestionsInRangeMutation({
		onSuccess: () => {
			invalidateAllContentCalendarSuggestions();
			onCleared?.();
		},
		onError,
	});

	const requestClear = () => {
		const range = getAiCalendarClearRange();
		setPreviewedRange(range);
		previewClear({ ...range, impersonationContext });
	};

	const onConfirmClose = (option?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		setPreviewCount(null);
		setPreviewedRange(null);

		if (canceled || !option?.representedObject || !previewedRange) {
			return;
		}

		archiveInRange({ ...previewedRange, impersonationContext });
	};

	return {
		confirmOptions: previewCount ? ClearOptions : NothingToClearOptions,
		isConfirmOpen: previewCount !== null,
		isPending: isPreviewing || isArchiving,
		onConfirmClose,
		previewCount: previewCount ?? 0,
		requestClear,
	};
};
